import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';

import cx from 'classnames';
import { append, clone, last, pipe, slice } from 'ramda';

import { parseStringToDate } from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/EventCalendar/Calendar/utils/days-generator/helpers';
import { TDayTripEvent } from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/types';
import {
  displayTravelBriefDate,
  displayTravelBriefGroupedDate,
} from 'detailsv2/LandingV2/Page/RightSide/blocks/Overview/TravelBriefTable/functions';
import {
  ITravelBriefDay,
  TDateRange,
  WithValue,
} from 'detailsv2/LandingV2/Page/RightSide/blocks/Overview/TravelBriefTable/types';
import i18n from 'i18n';
import { getDateFormat } from 'utils/date';
import { getTranslationDateFormat } from 'utils/i18n/date';

import { EDateFormat } from '../../../../structure';
import { PDFDataContext } from '../../../context';
import { ActivitiesCell } from './ActivitiesCell';
import s from './index.module.css';
import { NoEvents } from './NoEvents';

// @ts-ignore
export const useTravelBriefTable = ({ onReady }) => {
  const { page } = useContext(PDFDataContext);
  const dayRows = page?.travelBrief?.overall;
  const columns = useColumns({ isFlightTable: false });
  const currentDayIndex = useRef(0);
  const currentActivityIndex = useRef(0);
  const [pages, setPages] = useState(() => [[getDayWithActivity(dayRows, currentDayIndex.current)]]);
  const currentPageIndex = useRef(0);
  const allActivitiesProcessed = useCallback(
    () =>
      currentDayIndex.current === dayRows.length - 1 &&
      currentActivityIndex.current === last(dayRows)!.activities.length - 1,
    [dayRows],
  );
  const allActivitiesForCurrentDayUsed = useCallback(
    () =>
      dayRows[currentDayIndex.current].activities.length === 0 ||
      currentActivityIndex.current === dayRows[currentDayIndex.current].activities.length - 1,
    [dayRows],
  );

  const handleReady = useCallback(() => {
    // if we have processed all days and all activities
    if (allActivitiesProcessed()) return onReady();
    const newPages = clone(pages);

    // if we have used all activities for current day
    if (allActivitiesForCurrentDayUsed()) {
      // choose the next day
      ++currentDayIndex.current;
      // choose first activity
      currentActivityIndex.current = 0;

      newPages[currentPageIndex.current].push(getDayWithActivity(dayRows, currentDayIndex.current));
    } else {
      // if we are processing somewhere in the middle
      // just adding the next activity
      ++currentActivityIndex.current;
      const nextActivity = clone(dayRows[currentDayIndex.current].activities[currentActivityIndex.current]);

      const currentPage = newPages[currentPageIndex.current];
      currentPage[currentPage.length - 1].activities.push(nextActivity);
    }

    setPages(newPages);
  }, [pages, onReady, dayRows, allActivitiesForCurrentDayUsed, allActivitiesProcessed]);

  const handleFail = useCallback(() => {
    const currentPage = pages[currentPageIndex.current];

    // 1 case - if current page can not fit a day with a single activity then just ignore it and move on
    if (currentPage.length === 1 && currentPage[0].activities.length === 1) {
      if (allActivitiesProcessed()) return onReady();

      const newPages = append([], pages);
      currentPageIndex.current++;

      // if we have used all activities for current day
      if (allActivitiesForCurrentDayUsed()) {
        // choose the next day
        ++currentDayIndex.current;
        // choose first activity
        currentActivityIndex.current = 0;

        newPages[currentPageIndex.current].push(getDayWithActivity(dayRows, currentDayIndex.current));
      } else {
        // if we are processing somewhere in the middle
        // just adding the next activity
        ++currentActivityIndex.current;
        newPages[currentPageIndex.current].push(
          getDayWithActivity(dayRows, currentDayIndex.current, currentActivityIndex.current),
        );
      }

      return setPages(newPages);
    }

    // 2 case - any current day - any current non-first activity
    const newPages = clone(pages);
    const newCurrentPage = newPages[currentPageIndex.current];
    const lastDayOnPage = newCurrentPage[newCurrentPage.length - 1];

    // if we have only one activity for the last day on the page
    if (lastDayOnPage.activities.length <= 1) {
      // then we must cut it at all
      const lastDay = newCurrentPage.pop();

      currentPageIndex.current++;
      newPages.push([lastDay!]);
    } else if (lastDayOnPage.activities.length > 1) {
      const lastDay = newCurrentPage.pop();
      const problemActivity = lastDay?.activities.pop();
      const dayForTheNextPage = clone(lastDay);
      dayForTheNextPage!.activities = [problemActivity!];

      newPages[currentPageIndex.current].push(lastDay!);

      currentPageIndex.current++;

      newPages.push([dayForTheNextPage!]);
    }
    setTimeout(() => {
      setPages(newPages);
    }, 10);
  }, [pages, allActivitiesForCurrentDayUsed, allActivitiesProcessed, dayRows, onReady]);

  return { pages, columns, handleReady, handleFail };
};

function getDayWithActivity(days: ITravelBriefDay<TDateRange>[], dayNumber: number, activityNumber = 0) {
  const day = clone(days[dayNumber]);
  const activity = clone(day.activities[activityNumber]);

  if (activity) {
    day.activities = [activity];
  }

  return day;
}

const transformDate = pipe(slice(0, 10), parseStringToDate);

export function CellDate({ date, dateFormat }: { date: string; dateFormat?: EDateFormat }) {
  const format = '%A ' + getTranslationDateFormat(dateFormat);
  const dateParts = useMemo(() => i18n.strftime(transformDate(date), format).split(' '), [date, i18n.version]);

  return (
    <div className={s.date}>
      {dateParts.map((part, index) => (
        <span key={index}>
          {part + ' '}
          {index === 0 && <br />}
        </span>
      ))}
    </div>
  );
}

export function useColumns({ isFlightTable = false }: { isFlightTable?: boolean }) {
  const { page } = useContext(PDFDataContext);
  const displayDate = isFlightTable ? displayTravelBriefDate : displayTravelBriefGroupedDate;
  const timeFormat = page?.settings?.displayTimes24h ? '24h' : '12h';

  return useMemo(
    () => [
      {
        Header: i18n.t('proposal_content.travel_brief.tabs.overview.tabs.day'),
        accessor: 'dates',
        fixedSize: '18%',
        className: cx(s.thDay, 'custom-manifest-thDate'),
        Cell: displayDate(page.scheduleType, (date: string) => (
          <CellDate date={date} dateFormat={getDateFormat(page.settings?.displayDatesDdMmYyyy)} />
        )),
      },
      {
        Header: isFlightTable
          ? i18n.t('proposal_content.travel_brief.tabs.flight.tabs.flight')
          : i18n.t('proposal_content.travel_brief.tabs.overview.tabs.activities'),
        accessor: 'activities',
        fixedSize: '82%',
        className: cx(s.thAct, 'custom-manifest-thAcc'),
        Cell: ({ value }: WithValue<TDayTripEvent[]>) => {
          if (value.length === 0) return <NoEvents />;

          return value.map((tripEvent, i) => {
            return (
              <ActivitiesCell
                key={tripEvent.id + '_' + i}
                tripEvent={tripEvent}
                data={value}
                isFlightTable={isFlightTable}
                timeFormat={timeFormat}
              />
            );
          });
        },
      },
    ],
    [isFlightTable, page.scheduleType, i18n.version],
  );
}
